import { ref, reactive, computed } from 'vue';
import {
  fetchDepartmentPositionOptions,
  type PositionDepartmentOption,
} from './fetchDepartmentPositionOptions';

export type { PositionDepartmentOption };

export function usePositionOptionsCache() {
  const positionOptionsCache = reactive<Record<string | number, PositionDepartmentOption[]>>({});
  const loadingDepartmentIds = ref(new Set<number>());

  const isLoadingPositions = computed(() => loadingDepartmentIds.value.size > 0);

  const loadPositionsForDepartment = async (departmentId: number) => {
    if (positionOptionsCache[departmentId]) return;

    const nextLoading = new Set(loadingDepartmentIds.value);
    nextLoading.add(departmentId);
    loadingDepartmentIds.value = nextLoading;

    try {
      positionOptionsCache[departmentId] = await fetchDepartmentPositionOptions(departmentId);
    } finally {
      const done = new Set(loadingDepartmentIds.value);
      done.delete(departmentId);
      loadingDepartmentIds.value = done;
    }
  };

  const getPositionOptions = (departmentId: string | number): PositionDepartmentOption[] => {
    if (!departmentId) return [];
    return positionOptionsCache[departmentId] ?? [];
  };

  const isDepartmentLoadingPositions = (departmentId: string | number): boolean => {
    if (!departmentId) return false;
    return loadingDepartmentIds.value.has(Number(departmentId));
  };

  const clearCache = () => {
    Object.keys(positionOptionsCache).forEach((key) => {
      delete positionOptionsCache[key];
    });
  };

  return {
    positionOptionsCache,
    isLoadingPositions,
    isDepartmentLoadingPositions,
    loadPositionsForDepartment,
    getPositionOptions,
    clearCache,
  };
}
